import { FunctionComponent, memo } from 'react';
import lMessages from '../Messages';
import { getHighlightedText } from '../../../../utils/textSearch';
import Icon from '../../../Icon/Icon';
import { ChangeLimitationType } from '../LimitationManager';
import ButtonGroup, {
  LimitationButtonGroupItem,
} from '../LimitationButtonGroup/ButtonGroup';
import notify from '../../../Notify';
import Badge from '../../../Badge/Badge';
import { stringToColour } from '../../../../utils/style/colors';
import {
  ILimitationState,
  LimitationType,
} from 'store/limitations/limitationsSlice';
import { IUser } from 'store/users/usersType';
import { ActivePermissions } from 'store/providers/providerTypes';
import { ICompanyMember } from 'store/companies/companiesTypes';

type ComponentProps = {
  isDisabled: boolean;
  isOwner: boolean;
  onChangeLimitation: (
    limitationType: LimitationType,
    checked: boolean,
    itemType: ChangeLimitationType,
    limitationId: number,
    itemId: number
  ) => void;
  user: IUser;
  readLimitation: ILimitationState;
  reportLimitation: ILimitationState;
  changeDataLimitation: ILimitationState;
  editLimitation: ILimitationState;
  loadingForRead: boolean;
  loadingForReport: boolean;
  loadingForChangeData: boolean;
  loadingForEdit: boolean;
  parentActivePermission?: ActivePermissions;
  filterString?: string;
  showEditLimitation: boolean;
  showReportLimitation: boolean;
  showChangeDataLimitation: boolean;
  showReadLimitation: boolean;
  fillTextOverwrite?: string;
  companyMember?: ICompanyMember;
};

const LimitationUserRow: FunctionComponent<ComponentProps> = ({
  user,
  isDisabled,
  onChangeLimitation,
  readLimitation,
  reportLimitation,
  changeDataLimitation,
  editLimitation,
  loadingForRead,
  loadingForReport,
  loadingForChangeData,
  loadingForEdit,
  parentActivePermission,
  filterString,
  showEditLimitation,
  showReportLimitation,
  showChangeDataLimitation,
  showReadLimitation,
  companyMember,
  fillTextOverwrite,
}) => {
  const itemId = user.id;
  const editPermissionFromParent =
    parentActivePermission?.effectiveEditPermission?.includes('U' + itemId);
  const changeDataPermissionFromParent =
    parentActivePermission?.effectiveChangeDataPermission?.includes(
      'U' + itemId
    );
  const reportPermissionFromParent =
    parentActivePermission?.effectiveReportPermission?.includes('U' + itemId);
  const readPermissionFromParent =
    parentActivePermission?.effectiveReadPermission?.includes('U' + itemId);
  const canEditEdit = !editPermissionFromParent;
  const canEditChangeData = canEditEdit && !changeDataPermissionFromParent;
  const canEditReport = canEditChangeData && !reportPermissionFromParent;
  const canEditRead = canEditReport && !readPermissionFromParent;
  const readChecked = readLimitation?.exceptedUsers.some((id) => id === itemId);
  const reportChecked = reportLimitation?.exceptedUsers.some(
    (id) => id === itemId
  );
  const changeDataChecked = changeDataLimitation?.exceptedUsers.some(
    (id) => id === itemId
  );
  const editChecked = editLimitation?.exceptedUsers.some((id) => id === itemId);
  const selectedValue = editChecked
    ? 'edit'
    : changeDataChecked
      ? 'changeData'
      : reportChecked
        ? 'report'
        : readChecked
          ? 'read'
          : 'none';
  const handleChangeSelect = (selected?: LimitationButtonGroupItem) => {
    if (selected?.disabled || selected?.fromParent) {
      notify(lMessages.permissionIsFromParent, 'warning');
      return;
    }
    const value = selected?.type || 'none';
    function process(
      readChecked: boolean,
      limitationType: LimitationType,
      limitationId: number
    ) {
      const itemType = 'user' as ChangeLimitationType;
      if (!readChecked && value === limitationType)
        onChangeLimitation(
          limitationType,
          true,
          itemType,
          limitationId,
          itemId
        );
      setTimeout(() => {
        if (readChecked && value !== limitationType)
          onChangeLimitation(
            limitationType,
            false,
            itemType,
            limitationId,
            itemId
          );
      }, 100);
    }
    process(readChecked, 'read', readLimitation?.id);
    process(reportChecked, 'report', reportLimitation?.id);
    process(changeDataChecked, 'changeData', changeDataLimitation?.id);
    process(editChecked, 'edit', editLimitation?.id);
  };
  const permissionOptions = [] as LimitationButtonGroupItem[];
  if (showReadLimitation)
    permissionOptions.push({
      fromParent: !!readPermissionFromParent,
      selected: selectedValue === 'read',
      type: 'read',
      disabled: isDisabled || !canEditRead,
      titleOverwrite: fillTextOverwrite,
    });
  if (showReportLimitation)
    permissionOptions.push({
      fromParent: !!reportPermissionFromParent,
      selected: selectedValue === 'report',
      type: 'report',
      disabled: isDisabled || !canEditReport,
    });
  if (showChangeDataLimitation)
    permissionOptions.push({
      fromParent: !!changeDataPermissionFromParent,
      selected: selectedValue === 'changeData',
      type: 'changeData',
      disabled: isDisabled || !canEditChangeData,
    });
  if (showEditLimitation)
    permissionOptions.push({
      fromParent: !!editPermissionFromParent,
      selected: selectedValue === 'edit',
      type: 'edit',
      disabled: isDisabled || !canEditEdit,
    });
  return (
    <>
      <div id="--user_name--">
        <div style={{ marginRight: '17px', maxWidth: '300px' }}>
          <Icon type="user" style={{ margin: '0 10px' }} />
          {getHighlightedText(user.name, filterString)}
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {Object.values(companyMember?.roles || {}).map((it) => {
              return (
                <Badge key={it.id} color={stringToColour(it.name)}>
                  {getHighlightedText(it.name, filterString)}
                </Badge>
              );
            })}
          </div>
        </div>
      </div>

      <div>
        <ButtonGroup
          style={{}}
          width="undefined"
          onSelect={handleChangeSelect}
          items={permissionOptions}
          loading={
            loadingForRead ||
            loadingForReport ||
            loadingForChangeData ||
            loadingForEdit
          }
        />
      </div>
    </>
  );
};

export default memo(LimitationUserRow);
